import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { Testids } from '@mono/ui-selectors';
import { CommitResultCard } from './CommitResultCard';
import { ImportRecapCard } from './ImportRecapCard';
import { NextWorkstreamCard } from './NextWorkstreamCard';
import { OverviewCard } from './OverviewCard';
import { ProposalReviewCard } from './ProposalReviewCard';
import { ProposalRevisedCard } from './ProposalRevisedCard';
import { useDeliverableImportReview } from './useReview';
import { WorkstreamIntroCard } from './WorkstreamIntroCard';
import { WorkstreamSummaryCard } from './WorkstreamSummaryCard';
const KIND_LABELS = {
    overview: 'Overview',
    workstream_intro: 'Workstream intro',
    proposal_review: 'Proposal review',
    proposal_revised: 'Proposal revised',
    workstream_summary: 'Workstream summary',
    commit_result: 'Commit result',
    next_workstream: 'Next workstream',
    paused_recap: 'Paused recap',
    import_complete: 'Import complete'
};
/**
 * Dispatcher for the deliverable-import chat card family. Owns the shared
 * loading/error states for GetDeliverableImportReview; renders the real body
 * for every kind. PAUSED_RECAP and IMPORT_COMPLETE share one component
 * (ImportRecapCard) with a mode, since they differ only in wording/emphasis.
 * Never renders numbers from `payload` itself — the proto's
 * DeliverableImportCard doc comment is explicit that cards carry ids only
 * and the UI renders from the review response.
 */ function CardBody({ payload, review }) {
    if (!review) {
        return /*#__PURE__*/ _jsx("div", {
            className: "text-sm text-gray-400 mt-1",
            children: "…"
        });
    }
    switch(payload.kind){
        case 'overview':
            return /*#__PURE__*/ _jsx(OverviewCard, {
                review: review
            });
        case 'workstream_intro':
            return /*#__PURE__*/ _jsx(WorkstreamIntroCard, {
                payload: payload,
                review: review
            });
        case 'proposal_review':
            return /*#__PURE__*/ _jsx(ProposalReviewCard, {
                payload: payload,
                review: review
            });
        case 'proposal_revised':
            return /*#__PURE__*/ _jsx(ProposalRevisedCard, {
                payload: payload,
                review: review
            });
        case 'workstream_summary':
            return /*#__PURE__*/ _jsx(WorkstreamSummaryCard, {
                payload: payload,
                review: review
            });
        case 'commit_result':
            return /*#__PURE__*/ _jsx(CommitResultCard, {
                payload: payload,
                review: review
            });
        case 'next_workstream':
            return /*#__PURE__*/ _jsx(NextWorkstreamCard, {
                payload: payload,
                review: review
            });
        case 'paused_recap':
            return /*#__PURE__*/ _jsx(ImportRecapCard, {
                mode: "paused",
                payload: payload,
                review: review
            });
        case 'import_complete':
            return /*#__PURE__*/ _jsx(ImportRecapCard, {
                mode: "complete",
                payload: payload,
                review: review
            });
        default:
            return /*#__PURE__*/ _jsx("div", {
                className: "text-sm text-gray-400 mt-1",
                children: "…"
            });
    }
}
export function DeliverableImportCard({ payload }) {
    const { data, isLoading, error, refetch } = useDeliverableImportReview(payload.programId, payload.changeSetId, payload.workstreamKey || undefined);
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": Testids.deliverableImport.card,
            "data-kind": payload.kind,
            "data-workstream-key": payload.workstreamKey || undefined,
            className: "border rounded-lg p-3 bg-white",
            children: /*#__PURE__*/ _jsx("div", {
                className: "animate-pulse text-sm text-gray-400",
                children: "Loading review…"
            })
        });
    }
    if (error) {
        if (error instanceof ConnectError && error.code === Code.PermissionDenied) {
            return /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.deliverableImport.card,
                "data-kind": payload.kind,
                "data-workstream-key": payload.workstreamKey || undefined,
                className: "border rounded-lg p-3 bg-white",
                children: /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.deliverableImport.permissionDenied,
                    className: "text-sm text-gray-600",
                    children: "Only the Program Lead can review this deliverable import."
                })
            });
        }
        const message = error instanceof Error ? error.message : String(error);
        return /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.deliverableImport.card,
            "data-kind": payload.kind,
            "data-workstream-key": payload.workstreamKey || undefined,
            className: "border border-red-200 rounded-lg p-3 bg-red-50",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    "data-testid": Testids.deliverableImport.error,
                    className: "text-sm text-red-700 mb-2",
                    children: message
                }),
                /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    "data-testid": Testids.deliverableImport.retry,
                    onClick: ()=>refetch(),
                    className: "text-xs font-medium text-red-700 bg-red-100 rounded-md px-2 py-1 hover:bg-red-200",
                    children: "Retry"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.deliverableImport.card,
        "data-kind": payload.kind,
        "data-workstream-key": payload.workstreamKey || undefined,
        className: "border rounded-lg p-3 bg-white",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "text-xs font-medium uppercase tracking-wide text-gray-500",
                children: KIND_LABELS[payload.kind]
            }),
            payload.message && /*#__PURE__*/ _jsx("div", {
                className: "text-sm text-gray-700 mt-1",
                children: payload.message
            }),
            /*#__PURE__*/ _jsx(CardBody, {
                payload: payload,
                review: data
            })
        ]
    });
}
